import { createSlice } from '@reduxjs/toolkit'
import {
  PendingUsers,
  UserDetail,
  UserProfile,
  UserProfilePermissions,
  UserProfileReportPermissions,
} from 'types/global'
import { UserState, PayloadAction } from 'types/store'
import {
  getPendingUsers,
  getUserDetail,
  getUserProfilePermissions,
  getUserProfiles,
  getUserPermission,
  getUsersFilter,
  getUsers,
  updateUserDetail,
} from 'services/user'

import {
  FullPermissionKeys,
  GetUserDetailResponse,
  GetUserProfilesResponse,
  Permissions,
  UsersFilterResponse,
  GetUsersResponse,
  GetUserProfilePermissionsResponse,
  GetPendingUsersResponse,
  GetUserProfileReportPermissionsResponse,
} from 'services/types/user'
import { setLoading, setSuccess, unSet } from 'api/remoteData'
import { PERMISSIONS } from 'utils/constants/permissions'

import { Users } from './types/user'
import { QUOTATION_APPROVAL_LEVEL_FALLBACK, USER_STATUS } from 'utils/constants'

const initialState: UserState = {
  pendingUsers: unSet(),
  selectedBranch: undefined,
  user: undefined,
  userBranches: [],
  userDetail: unSet(),
  userProfilePermissions: unSet(),
  userProfiles: unSet(),
  userPermissions: unSet(),
  usersFilter: unSet(),
  registeredUsers: unSet(),
}

const { reducer, actions } = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, { payload: { user, userBranches } }) => ({
      ...state,
      user,
      userBranches,
      selectedBranch: userBranches ? (userBranches.length === 1 ? userBranches[0] : null) : null,
    }),
    updateUser: (state, { payload }) => ({
      ...state,
      user: { ...payload },
    }),
    setUserPermissions: (state, { payload }) => ({
      ...state,
      userPermissions: setSuccess(mapUserPermissions(payload)),
    }),
    setUserDetail: (state, { payload }) => ({
      ...state,
      userDetail: payload,
    }),
    setSelectedBranch: (state, { payload }) => ({
      ...state,
      selectedBranch: payload,
    }),
    clear: () => ({ ...initialState, selectedBranch: undefined, user: undefined }),
    clearPermissions: (state) => ({
      ...state,
      userProfilePermissions: initialState.userProfilePermissions,
    }),
    clearUsersFilter: (state) => ({
      ...state,
      usersFilter: initialState.usersFilter,
    }),
    clearRegUsers: (state) => ({
      ...state,
      registeredUsers: initialState.registeredUsers,
    }),
  },
  extraReducers: (builder) => {
    const userDetailPendingStatus = (state: UserState, { meta }: PayloadAction) => ({
      ...state,
      userDetail: setLoading(meta.requestId),
    })

    builder
      .addCase(getUserDetail.pending, userDetailPendingStatus)
      .addCase(getUserDetail.fulfilled, (state, { meta, payload }) => {
        if (payload.status === 'FAIL') {
          return {
            ...state,
            userDetail: payload,
          }
        }
        return {
          ...state,
          userDetail: setSuccess(mapUserDetail(payload.data), meta.requestId),
        }
      })

    builder
      .addCase(getUserProfiles.pending, (state) => ({ ...state, userProfiles: setLoading() }))
      .addCase(getUserProfiles.fulfilled, (state, { payload }) => {
        if (payload.status === 'FAIL') {
          return { ...state, userProfiles: payload }
        }

        return { ...state, userProfiles: setSuccess(mapUserProfiles(payload.data)) }
      })

    builder
      .addCase(getUserProfilePermissions.pending, (state) => ({ ...state, userProfilePermissions: setLoading() }))
      .addCase(getUserProfilePermissions.fulfilled, (state, { payload }) => {
        if (payload.status === 'FAIL') {
          return { ...state, userProfilePermissions: payload }
        }
        return { ...state, userProfilePermissions: setSuccess(mapUserProfilePermissions(payload.data)) }
      })

    builder
      .addCase(getUserPermission.pending, (state) => ({ ...state, userProfilePermissions: setLoading() }))
      .addCase(getUserPermission.fulfilled, (state, { payload }) => {
        if (payload.status === 'FAIL') {
          return { ...state, userPermissions: payload }
        }
        return { ...state, userPermissions: setSuccess(mapUserPermissions(payload.data.data)) }
      })

    builder
      .addCase(getPendingUsers.pending, (state) => ({ ...state, pendingUsers: setLoading() }))
      .addCase(getPendingUsers.fulfilled, (state, { payload }) => {
        if (payload.status === 'FAIL') {
          return { ...state, pendingUsers: payload }
        }

        return { ...state, pendingUsers: setSuccess(mapPendingUsers(payload.data)) }
      })

    builder
      .addCase(getUsersFilter.pending, (state) => ({ ...state, usersFilter: setLoading() }))
      .addCase(getUsersFilter.fulfilled, (state, { payload }) => {
        if (payload.status === 'FAIL') {
          return { ...state, usersFilter: payload }
        }

        return { ...state, usersFilter: setSuccess(mapUsersFilter(payload.data)) }
      })

    builder
      .addCase(getUsers.pending, (state) => ({ ...state, registeredUsers: setLoading() }))
      .addCase(getUsers.fulfilled, (state, { payload }) => {
        if (payload.status === 'FAIL') {
          return { ...state, registeredUsers: payload }
        }

        return { ...state, registeredUsers: setSuccess(getMappedUserData(payload.data)) }
      })

    builder.addCase(updateUserDetail.fulfilled, (state, { payload }) => {
      if (payload.status === 'SUCCESS') {
        return {
          ...state,
          userDetail: setSuccess(mapUserDetail(payload.data)),
        }
      }

      return state
    })
  },
})

const mapPendingUsers: (data: GetPendingUsersResponse) => PendingUsers = ({ num_rows: { count: numRows }, data }) => ({
  numRows,
  data: data.map((i) => ({
    createTs: i.create_ts,
    email: i.email,
    mobile: {
      countryCode: Number(i.mobile.prefix),
      countryISO: i.mobile.region,
      number: i.mobile.value,
    },
    firstName: i.fname,
    lastName: i.lname,
    userId: i.user_id,
  })),
})

const HIDDEN_PERMISSION = [
  'CREATE_BRANCH',
  'VIEW_BRANCH',
  'EDIT_BRANCH',
  'DEACTIVATE_BRANCH',
  'DELETE_BRANCH',
  'CREATE_DEPARTMENT',
  'VIEW_DEPARTMENT',
  'EDIT_DEPARTMENT',
  'DELETE_DEPARTMENT',
  'DELETE_USER',
]

export const mapUserProfilePermissions: (data: GetUserProfilePermissionsResponse) => UserProfilePermissions = ({
  data,
}) =>
  data.map((group) => ({
    groupName: group.group_name,
    subgroups: group.subgroups.map((subgroup) => ({
      subgroupName: subgroup.subgroup_name,
      permissions: subgroup.permissions
        .filter((i) => (!HIDDEN_PERMISSION.includes(i.permission_name) ? true : false))
        .map((permission) => ({
          description: permission.description,
          permissionId: permission.permission_id,
          permissionName: permission.permission_name,
          action: permission.action,
          hasAccess: permission.has_access,
        })),
    })),
  }))

export const mapUserProfileReportPermissions: (
  data: GetUserProfileReportPermissionsResponse,
) => UserProfileReportPermissions = ({ permissions }) =>
  permissions.map((permission) => ({
    reportId: permission.report_id,
    title: permission.title,
    hasAccess: permission.has_access,
  }))

export const mapUserProfiles: (data: GetUserProfilesResponse) => UserProfile[] = ({ data }) =>
  data.map((p) => ({
    userProfileId: p.user_profile_id,
    userProfileName: p.user_profile_name,
    isDefault: p.is_default,
  }))

export const mapUserDetail: (data: GetUserDetailResponse) => UserDetail = (data) => ({
  approver: data.approver_name,
  branches: data.branches.map((b) => ({
    branchId: b.branch_id,
    branchName: b.branch_name,
    countryCode: b.country_code,
  })),
  contractEnd: data.contract_end,
  contractStart: data.contract_start,
  createTs: data.create_ts,
  deactivateOnContractEnd: data.deactivate_on_contract_end,
  email: data.email,
  employeeId: data.employee_id,
  firstName: data.fname,
  lastName: data.lname,
  lastLogin: data.last_login_ts,
  photoUrl: data.photo_url || undefined,
  isActive: data.is_active,
  isVerified: data.is_verified,
  mobile: {
    countryCode: Number(data.mobile.prefix),
    countryISO: data.mobile.region,
    number: data.mobile.value,
  },
  userId: data.user_id,
  userProfile: {
    userProfileId: data.user_profile_id,
    userProfileName: data.user_profile_name,
  },
  deactivateTs: data.deactivate_ts,
  camsSalesAgents: data.cams_sales_agents?.map((i) => ({
    camsSalesAgentId: i.cams_sales_agent_id,
    camsHostId: i.cams_host_id,
    name: i.name,
  })),
  sip: data.sip,
  dialerUrl: data.dialer_url,
  jobTitle: data.job_title,
  location: data.location,
  twoFactorOptions: data.auth_2fa,
  twoFactorId: data.auth_2fa_id,
  quotationApprovalLevel: data.quotation_approval_level.map((i) => ({
    approvalLevel: i.approval_level || QUOTATION_APPROVAL_LEVEL_FALLBACK,
    branchId: i.branch_id,
  })),
  supervisorId: data.supervisor_id,
  departmentId: data.department_id,
  departmentName: data.department_name,
  departmentTypeId: data.department_type_id,
  departmentTypeName: data.department_type_name,
  teams: data.teams?.map((team) => ({ teamId: team.team_id, teamName: team.team_name })) ?? [],
})

export const mapUserPermissions = (data: Array<FullPermissionKeys>) =>
  data?.reduce((acc, cur) => {
    const currentPermission = PERMISSIONS[cur]

    // In case FE hard code value and BE response does not match
    if (!currentPermission) return { ...acc }

    const { groupName, actionKey } = currentPermission

    if (!acc[groupName]) {
      acc[groupName] = { [`${actionKey}`]: true }
    } else {
      const currPermissionGroup = { ...acc[groupName] }
      acc[groupName] = { ...currPermissionGroup, [`${actionKey}`]: true }
    }

    return { ...acc }
  }, {} as Permissions) ?? {}

const mapUsersFilter = (data: UsersFilterResponse) => ({
  profile: {
    options: data.profiles.map((profile) => ({ value: profile.user_profile_id, label: profile.user_profile_name })),
    value: [],
  },
  team: {
    options:
      data.teams?.map((team) => ({
        value: team.team_id,
        label: team.team_name,
      })) || [],
    value: [],
  },
  status: {
    options: data.statuses.map((status) => {
      switch (status) {
        case 'ACTIVE':
          return { value: 'ACTIVE', label: 'active_lbl' }
        case 'PENDING_VERIFICATION':
          return { value: 'PENDING_VERIFICATION', label: 'Pending Verification' }
        case 'INACTIVE':
          return { value: 'INACTIVE', label: 'Inactive' }
        default:
          return { value: 'ACTIVE', label: 'active_lbl' }
      }
    }),
    value: [],
  },
})

export const getMappedUserData: (response: GetUsersResponse) => Users = (response) => {
  const { data, num_rows } = response

  return {
    numberRow: num_rows,
    userDetails: data.map((userDetail) => ({
      userId: userDetail.user_id,
      userProfileName: userDetail.user_profile_name,
      fName: userDetail.fname,
      lName: userDetail.lname,
      email: userDetail.email,
      photoUrl: userDetail.photo_url,
      branch: userDetail.branch_name,
      status: USER_STATUS[userDetail.status],
    })),
  }
}

export default reducer
export { actions }
